import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { fuelService } from "@/services/fuels/fuelService";
import { FuelAction } from "@/types/fuels/fuels.types";

interface UseConfirmationDialogProps {
  onSuccess: () => void;
}

export const useConfirmationDialog = ({ onSuccess }: UseConfirmationDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<FuelAction | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const openDialog = (type: "archive" | "restore", fuelId: string, fuelName: string) => {
    setConfirmAction({ type, fuelId, fuelName });
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setConfirmAction(null);
  };

  const handleConfirm = async (action: FuelAction) => {
    setIsLoading(true);

    try {
      if (action.type === "archive") {
        await fuelService.archive(action.fuelId, user?.uid);
        toast({
          title: t("success"),
          description: t("fuels.archived"),
        });
      } else {
        await fuelService.restore(action.fuelId, user?.uid);
        toast({
          title: t("success"),
          description: t("fuels.restored"),
        });
      }

      onSuccess();
      closeDialog();
    } catch (error: any) {
      console.error("Error handling fuel action:", error);
      toast({
        title: t("error"),
        description: action.type === "archive" ? t("fuels.archiveError") : t("fuels.restoreError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isOpen,
    confirmAction,
    isLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  };
}; 